import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isSupabaseConfigured, supabase } from "../supabaseClient";

const CONTENT_SOURCES = {
  "programmes.json": {
    preferJson: true,
    table: "programmes",
    transform: (row) => ({
      id: row.item_id,
      cat: row.cat,
      days: row.days,
      people: row.people,
      old: row.old,
      price: row.price,
      img: row.img,
      title: row.title,
      location: row.location,
      type: row.type,
      includes: row.includes || [],
    }),
  },
  "destinations.json": {
    table: "destinations",
    transform: (row) => ({
      img: row.img,
      flag: row.flag,
      country: row.country,
      name: row.name,
      tag: row.tag,
      discount: row.discount,
      desc: row.desc,
      highlights: row.highlights || [],
    }),
  },
};

const getPublicPath = (fileName) => {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/content/${fileName}`;
};

const pickLocalizedContent = (content, language) => {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return content;
  }

  const shortLanguage = (language || "").split("-")[0];
  const localized = content[shortLanguage] || content[language];

  if (Array.isArray(localized) && localized.length > 0) {
    return localized;
  }

  return content.ar || content.fr || content.en || localized || null;
};

const useEditableContent = (fileName, fallback) => {
  const { i18n } = useTranslation();
  const [content, setContent] = useState(null);

  useEffect(() => {
    let active = true;
    const shortLanguage = (i18n.language || "").split("-")[0] || "fr";
    const source = CONTENT_SOURCES[fileName];

    const loadJsonFallback = () => {
      fetch(getPublicPath(fileName), { cache: "no-store" })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Could not load ${fileName}`);
          }
          return response.json();
        })
        .then((data) => {
          if (active) {
            setContent(data);
          }
        })
        .catch(() => {
          if (active) {
            setContent(null);
          }
        });
    };

    if (!source || !isSupabaseConfigured || !supabase) {
      loadJsonFallback();
      return () => {
        active = false;
      };
    }

    if (source.preferJson) {
      loadJsonFallback();
      return () => {
        active = false;
      };
    }

    supabase
      .from(source.table)
      .select("*")
      .eq("is_active", true)
      .eq("language", shortLanguage)
      .order("sort_order", { ascending: true })
      .then(({ data, error }) => {
        if (!active) {
          return;
        }

        if (error || !Array.isArray(data) || data.length === 0) {
          loadJsonFallback();
          return;
        }

        const rows = data.map(source.transform);

        if (rows.length > 0) {
          setContent(rows);
        } else {
          loadJsonFallback();
        }
      })
      .catch(() => {
        loadJsonFallback();
      });

    return () => {
      active = false;
    };
  }, [fileName, i18n.language]);

  return useMemo(() => {
    const localized = pickLocalizedContent(content, i18n.language);
    return localized || fallback;
  }, [content, fallback, i18n.language]);
};

export default useEditableContent;
